import { useCallback, useEffect, useMemo, useState } from "react";
import { useApiClient, RequestConfig } from "@/hooks";
import { Column, useTable, usePagination, useSortBy, TableOptions, useFilters, useRowSelect, TableInstance } from "react-table";

interface UseServerSideTableOptions<D extends object, Req> {
  tableOptions: Partial<TableOptions<D>> & { columns: ReadonlyArray<Column<D>> };
  requestConfig: RequestConfig<Req>;
}

const defaultTableOptions = {
  manualSortBy: true,
  manualPagination: true,
  manualFilters: true,
  defaultColumn: {
    Filter: () => null,
  },
};

function useUpdateTableData<T>() {
  const [data, setData] = useState<T[]>([]);
  const [skipPageReset, setSkipPageReset] = useState(false);

  const updateMyData = useCallback((rowIndex: number, columnId: number, value: any) => {
    setSkipPageReset(true);
    setData((old: any) => old.map((row: any, index: number) => {
      if (index === rowIndex) {
        return {
          ...old[rowIndex],
          [columnId]: value,
        };
      }
      return row;
    }));
  }, []);

  useEffect(() => {
    setSkipPageReset(false);
  }, [data]);

  return useMemo(
    () => ({ data, setData, updateMyData, skipPageReset }),
    [data, setData, updateMyData, skipPageReset]
  );
}

function useApiConfig<D extends object, Req>(requestConfig: RequestConfig<Req>, table: TableInstance<D>) {
  const sortBy = JSON.stringify(table.state.sortBy.map(({ id, desc }) => `${id},${desc ? "DESC" : "ASC"}`));
  const filter = JSON.stringify(table.state.filters.map(({ id, value }) => `${id},${value}`));
  const manualPagination = JSON.stringify(table.manualPagination ? { pageIndex: table.state.pageIndex, pageSize: table.state.pageSize } : {});

  return useMemo(() => ({
    ...requestConfig,
    params: {
      sortBy: JSON.parse(sortBy),
      filter: JSON.parse(filter),
      ...JSON.parse(manualPagination),
      ...requestConfig.params,
    },
  }), [requestConfig, manualPagination, sortBy, filter]);
}

export function useTableWithAuthApi<D extends object, Req>({ tableOptions, requestConfig }:
  UseServerSideTableOptions<D, Req>) {
  const { data, setData, updateMyData, skipPageReset } = useUpdateTableData<D>();
  const [pageCount, setPageCount] = useState<number>(0);

  const table = useTable(
    { data, pageCount, ...defaultTableOptions, ...tableOptions, autoResetPage: !skipPageReset, updateMyData },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
  );

  const apiConfig = useApiConfig(requestConfig, table);

  const { request, response, isLoading, error } = useApiClient(apiConfig);

  useEffect(() => {
    if (response?.data) {
      setData(response?.data.items);
      setPageCount(Math.ceil(response?.data.total / table.state.pageSize));
    }
  }, [response, setData, table.state.pageSize]);

  return useMemo(
    () => ({ table, data, request, isLoading, error }),
    [table, data, request, isLoading, error]
  );
}
